import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Subject } from "rxjs";
import { ExecutionResult, UdfContext, UserDefinedFieldViewModel } from "@tylertech/tyler-api-types";
import { Activatable } from "../../interfaces/userDefinedField";
import { UserDefinedFieldService } from "../../services";
import { Command, LabelCommand } from "@tylertech/commands-module";
import { Utilities } from "@tylertech/shared-module";
@Component({
    selector: "user-defined-field-list",
    templateUrl: "./userDefinedFieldList.tmpl.html",
    styleUrls: ["./userDefinedFieldList.component.scss"],
    standalone: false
})
export class UserDefinedFieldListComponent implements OnInit, OnDestroy
{
    private $userDefinedFieldsService = inject(UserDefinedFieldService);

    private readonly destructor$ = new Subject<void>();
    readonly userDefinedFieldForm: FormGroup;
    readonly commands: ReadonlyArray<Command>;
    @Input()
    private readonly userDefinedFields: ReadonlyArray<UserDefinedFieldViewModel & Activatable>;

    @Input()
    readonly settings: Readonly<UdfContext>;

    @Input()
    readonly viewOnly: boolean;

    @Output()
    readonly onClose = new EventEmitter<Array<UserDefinedFieldViewModel & Activatable> | undefined>();

    private _userDefinedFields: Array<UserDefinedFieldViewModel & Activatable>;
    isLoaded: boolean;
    errorMessage: string | undefined;
    constructor()
    {
        this.userDefinedFieldForm = new FormGroup({});
        this.commands = [
            new LabelCommand({
                label: "Cancel",
                action: () => this.cancel(),
                getEnabled: () => true,
            }),
            new LabelCommand({
                label: "Done",
                action: () => this.done(),
                getEnabled: () => this.userDefinedFieldForm.valid,
                tooltip: undefined,
                getVisible: () => !this.viewOnly,
            }),
        ];
    }

    get fields(): Array<UserDefinedFieldViewModel & Activatable>
    {
        return this._userDefinedFields;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit()
    {
        (this.userDefinedFields?.length > 0
            ? Promise.resolve(this.userDefinedFields)
            : this.$userDefinedFieldsService
                .Get(this.settings)
                .then((executionResult: ExecutionResult<Array<UserDefinedFieldViewModel>>) =>
                {
                    return executionResult.Result;
                })
        )
            .then((userDefinedFields: ReadonlyArray<UserDefinedFieldViewModel>) =>
            {
                this._userDefinedFields = Utilities.deepCopy(
                    userDefinedFields as Array<UserDefinedFieldViewModel>
                ).sort((n1, n2) => n1.Sort - n2.Sort);
                this._userDefinedFields.forEach(field =>
                {
                    if (field.Value || field.IsRequired || field.IncludeInTemplate)
                    {
                        if (!field.Value)
                        {
                            field.Value = "";
                        }
                        field.Active = true;
                    }
                });
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    onAddNewField(field: UserDefinedFieldViewModel & Activatable): void
    {
        field.Active = true;
        if (!field.Value)
        {
            field.Value = "";
        }
        // move the added Udf to end of this.udfs so it appears last in udf.list.tmpl.html
        const index = this._userDefinedFields.indexOf(field);
        const removedField = this._userDefinedFields.splice(index, 1)[0];
        this._userDefinedFields.push(removedField);
    }

    private cancel(): void
    {
        // ? TODO: why are we resetting our list on close? it will just be cleanup when the component is destroyed?
        this._userDefinedFields = this.userDefinedFields.map(x => ({ ...x }));
        this.onClose.emit();
    }

    private done(): void
    {
        if (this.userDefinedFieldForm.invalid)
        {
            this.errorMessage = "The User Defined Field(s) could not be saved.";
            return;
        }
        this.onClose.emit(this._userDefinedFields);
    }
}
